import { type WeaponId, isWeaponId, levelForExperience } from '@/data/weapons';

/**
 * How much each weapon has been used.
 *
 * Kept per weapon rather than per player, because that is what makes a blade
 * *yours*: carrying the Rootcleaver through ten floors should mean something
 * that forging a second one would not give you back.
 *
 * Saved, unlike almost everything else added recently. Dungeon floors, waves
 * and chest contents are all regenerated because they are places and events.
 * A weapon's history is neither — it is the only thing in a run that the
 * player is meant to be able to keep.
 */
export class WeaponGrowth {
  private readonly experience = new Map<WeaponId, number>();

  experienceFor(id: WeaponId): number {
    return this.experience.get(id) ?? 0;
  }

  levelFor(id: WeaponId): number {
    return levelForExperience(this.experienceFor(id));
  }

  /**
   * Record damage dealt, returning the new level when one was gained.
   *
   * Returning the level rather than a boolean means the caller can say *what*
   * was reached without asking a second question, and the null case reads as
   * "nothing worth mentioning happened".
   */
  gain(id: WeaponId, damage: number): number | null {
    if (damage <= 0) return null;

    const before = this.levelFor(id);
    this.experience.set(id, this.experienceFor(id) + damage);
    const after = this.levelFor(id);

    return after > before ? after : null;
  }

  /** Forget a weapon's practice, when the weapon itself is gone. */
  forget(id: WeaponId): void {
    this.experience.delete(id);
  }

  toSave(): Record<string, number> {
    return Object.fromEntries(this.experience);
  }

  static fromSave(value: unknown): WeaponGrowth {
    const growth = new WeaponGrowth();
    if (!value || typeof value !== 'object') return growth;

    for (const [id, experience] of Object.entries(value as Record<string, unknown>)) {
      // A weapon that no longer exists takes its history with it, rather than
      // sitting in the save forever waiting to be re-added.
      if (!isWeaponId(id)) continue;
      const amount = Math.floor(Number(experience));
      if (!Number.isFinite(amount) || amount <= 0) continue;
      growth.experience.set(id, amount);
    }

    return growth;
  }
}
